import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { RequireAuth, RequirePasswordReset, RequireRole } from "@/lib/auth/require-auth";
import { AuthenticatedLayout } from "@/components/layout/authenticated-layout";

// Guest pages (no auth required)
const LoginPage = lazy(() => import("@/features/auth/pages/login-page"));
const RequestAccessPage = lazy(() => import("@/features/auth/pages/request-access-page"));
const ResetPasswordPage = lazy(() => import("@/features/auth/pages/reset-password-page"));
const InvitationAcceptPage = lazy(() => import("@/features/auth/pages/invitation-accept-page"));
const ChangePasswordPage = lazy(() => import("@/features/auth/pages/change-password-page"));
const NotFoundPage = lazy(() => import("@/features/auth/pages/not-found-page"));

// Authenticated pages
const DashboardPage = lazy(() => import("@/features/dashboard/pages/dashboard-page"));
const AllOrdersPage = lazy(() => import("@/features/orders/pages/all-orders-page"));
const ManageOrdersPage = lazy(() => import("@/features/orders/pages/manage-orders-page"));
const ViewOrdersPage = lazy(() => import("@/features/orders/pages/view-orders-page"));
const ManifestImportPage = lazy(() => import("@/features/orders/pages/manifest-import-page"));
const OrderDetailPage = lazy(() => import("@/features/orders/pages/order-detail-page"));
const OrderHistoryPage = lazy(() => import("@/features/order-history/pages/order-history-page"));
const OrderHistoryEventPage = lazy(() => import("@/features/order-history/pages/order-history-event-page"));
const OrderDocPage = lazy(() => import("@/features/orders/pages/order-doc-page"));
const BuildOrderPage = lazy(() => import("@/features/orders/pages/build-order-page"));
const SubmitOrderPage = lazy(() => import("@/features/orders/pages/submit-order-page"));
const InvoiceListPage = lazy(() => import("@/features/invoices/pages/invoice-list-page"));
const InvoiceDetailPage = lazy(() => import("@/features/invoices/pages/invoice-detail-page"));
const MilestonePayPage = lazy(() => import("@/features/invoices/pages/milestone-pay-page"));
const PaymentReceiptsPage = lazy(() => import("@/features/invoices/pages/payment-receipts-page"));
const PaymentSuccessPage = lazy(() => import("@/features/invoices/pages/payment-success-page"));
const PaymentErrorPage = lazy(() => import("@/features/invoices/pages/payment-error-page"));

// Catalog
const ProductsPage = lazy(() => import("@/features/catalog/pages/products-page"));
const AttachmentsPage = lazy(() => import("@/features/catalog/pages/attachments-page"));
import { DoorStylesPage, WoodSpeciesPage, FinishOptionsPage, FinishedSidesPage } from "@/features/catalog/pages/premium-list-page";
const ConstructionMethodsPage = lazy(() => import("@/features/catalog/pages/construction-methods-page"));
const PublicCatalogPage = lazy(() => import("@/features/catalog/pages/public-catalog-page"));
const CategoryDetailPage = lazy(() => import("@/features/catalog/pages/category-detail-page"));
const SubcategoryDetailPage = lazy(() => import("@/features/catalog/pages/subcategory-detail-page"));
const Upload2020Page = lazy(() => import("@/features/catalog/pages/upload-2020-page"));
const CatalogBrowsePage = lazy(() => import("@/features/catalog/pages/catalog-browse-page"));

// Finance
const AgingReportPage = lazy(() => import("@/features/finance/pages/aging-report-page"));
const RevenueReportPage = lazy(() => import("@/features/finance/pages/revenue-report-page"));
const MonthlyClosePage = lazy(() => import("@/features/finance/pages/monthly-close-page"));
const PaymentConfigPage = lazy(() => import("@/features/finance/pages/payment-config-page"));
const DiscountConfigPage = lazy(() => import("@/features/finance/pages/discount-config-page"));

// Scanning
const ScanSessionsPage = lazy(() => import("@/features/scanning/pages/scan-sessions-page"));
const NewScanPage = lazy(() => import("@/features/scanning/pages/new-scan-page"));
const ScanSessionDetailPage = lazy(() => import("@/features/scanning/pages/scan-session-detail-page"));
const ActiveScanPage = lazy(() => import("@/features/scanning/pages/active-scan-page"));

// Settings
const ProfilePage = lazy(() => import("@/features/settings/pages/profile-page"));
const AddressesPage = lazy(() => import("@/features/settings/pages/addresses-page"));
const GlobalSettingsPage = lazy(() => import("@/features/settings/pages/global-settings-page"));
const ApiKeysPage = lazy(() => import("@/features/api-keys/pages/api-keys-page"));
const ClientDetailPage = lazy(() => import("@/features/api-keys/pages/client-detail-page"));
const OAuthConsentPage = lazy(() => import("@/features/oauth-consent/pages/oauth-consent-page"));
const AnnouncementPage = lazy(() => import("@/features/settings/pages/announcement-page"));

// Accounts
const AllUsersPage = lazy(() => import("@/features/accounts/pages/all-users-page"));
const StaffUsersPage = lazy(() => import("@/features/accounts/pages/staff-users-page"));
const AccessRequestsPage = lazy(() => import("@/features/accounts/pages/access-requests-page"));
const UserDetailPage = lazy(() => import("@/features/accounts/pages/user-detail-page"));
const UserInvoicesPage = lazy(() => import("@/features/accounts/pages/user-invoices-page"));
const CompanyDetailPage = lazy(() => import("@/features/accounts/pages/company-detail-page"));
const CompanyInvoicesPage = lazy(() => import("@/features/accounts/pages/company-invoices-page"));

// Order sub-views & reports
const CompletedOrdersPage = lazy(() => import("@/features/orders/pages/completed-orders-page"));
const PartiallyCompletedPage = lazy(() => import("@/features/orders/pages/partially-completed-page"));
const CompletionLogPage = lazy(() => import("@/features/orders/pages/completion-log-page"));
const CreatedOrdersPage = lazy(() => import("@/features/orders/pages/created-orders-page"));
const LateOrdersPage = lazy(() => import("@/features/orders/pages/late-orders-page"));
const RemakesPage = lazy(() => import("@/features/orders/pages/remakes-page"));
const CompletedCabinetsPage = lazy(() => import("@/features/orders/pages/completed-cabinets-page"));

const OrderScanSessionsPage = lazy(() => import("@/features/orders/pages/order-scan-sessions-page"));
const ProductionListPage = lazy(() => import("@/features/orders/pages/production-list-page"));

// Planner
const PlannerPage = lazy(() => import("@/features/planner/pages/planner-page"));

// Issues (AI-reported)
const IssuesListPage = lazy(() => import("@/features/issues/pages/issues-list-page"));
const IssueDetailPage = lazy(() => import("@/features/issues/pages/issue-detail-page"));

// Prompt admin (chat assistant prompts, versioned)
const PromptsListPage = lazy(() => import("@/features/prompts/pages/prompts-list-page"));
const PromptDetailPage = lazy(() => import("@/features/prompts/pages/prompt-detail-page"));

// Projects (OMS v2 - see OMS-9)
const ProjectsListPage = lazy(() => import("@/features/projects/pages/projects-list-page"));
const ProjectDetailPage = lazy(() => import("@/features/projects/pages/project-detail-page"));
const ProjectVersionsPage = lazy(() => import("@/features/projects/pages/project-proposals-page"));

// Proposals (OMS v2 — see OMS-5)
const ProposalTemplateSettingsPage = lazy(() => import("@/features/proposals/pages/proposal-template-settings-page"));
const ProposalTemplateEditPage = lazy(() => import("@/features/proposals/pages/proposal-template-edit-page"));

// Billing — Project invoices (OMS-6)
const ProjectInvoiceListPage = lazy(() => import("@/features/billing/pages/project-invoice-list-page"));
const ProjectInvoiceDetailPage = lazy(() => import("@/features/billing/pages/project-invoice-detail-page"));
const PayProjectInvoicePage = lazy(() => import("@/features/billing/pages/pay-project-invoice-page"));

// Customers (OMS v2 CRM - see OMS-10)
const CustomerAccountsPage = lazy(() => import("@/features/customers/pages/customer-accounts-page"));
const CustomerAccountCreatePage = lazy(() => import("@/features/customers/pages/customer-account-create-page"));
const CustomerAccountDetailPage = lazy(() => import("@/features/customers/pages/customer-account-detail-page"));
const ContactCreatePage = lazy(() => import("@/features/customers/pages/contact-create-page"));
const ContactDetailPage = lazy(() => import("@/features/customers/pages/contact-detail-page"));
const ContactsPage = lazy(() => import("@/features/customers/pages/contacts-page"));
const ContactRolesPage = lazy(() => import("@/features/customers/pages/contact-roles-page"));

function PageLoader() {
  return (
    <div className="flex min-h-svh items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Guest routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/request-access" element={<RequestAccessPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/invitation/:invite_uuid/accept" element={<InvitationAcceptPage />} />
          <Route path="/404" element={<NotFoundPage />} />

          {/* Public catalog browse (guest, no auth) */}
          <Route path="/catalog/browse" element={<CatalogBrowsePage />} />

          {/* Payment callback pages (guest) */}
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/error" element={<PaymentErrorPage />} />

          {/* Production list (guest-by-token, no auth) */}
          <Route path="/orders/print-lists/:token" element={<ProductionListPage />} />

          {/* Hosted project-invoice payment (guest-by-token, no auth) */}
          <Route path="/pay/invoice/:token" element={<PayProjectInvoicePage />} />

          {/* OAuth2 consent screen for MCP agents — authenticated, but
              standalone (no sidebar chrome) so the user focuses on the
              consent decision. Bounces to /login with returnTo if
              unauth. */}
          <Route
            path="/oauth/authorize"
            element={
              <RequireAuth>
                <OAuthConsentPage />
              </RequireAuth>
            }
          />

          {/* Authenticated routes */}
          <Route
            element={
              <RequireAuth>
                <RequirePasswordReset>
                  <AuthenticatedLayout />
                </RequirePasswordReset>
              </RequireAuth>
            }
          >
            {/* Change password (inside auth but before password reset check in RequirePasswordReset) */}
            <Route path="/settings/profile/change-password" element={<ChangePasswordPage />} />

            {/* Dashboard */}
            <Route path="/" element={<DashboardPage />} />

            {/* Orders */}
            <Route path="/orders" element={<AllOrdersPage />} />
            <Route path="/orders/all" element={<ViewOrdersPage />} />
            <Route path="/orders/manage" element={<ManageOrdersPage />} />
            <Route path="/orders/build" element={<BuildOrderPage />} />
            <Route path="/orders/remakes" element={<RemakesPage />} />
            <Route path="/orders/completed" element={<CompletedOrdersPage />} />
            <Route path="/orders/completed-cabinets" element={<CompletedCabinetsPage />} />
            <Route path="/orders/partially-completed" element={<PartiallyCompletedPage />} />
            <Route path="/orders/completion-log" element={<CompletionLogPage />} />
            <Route path="/orders/late" element={<LateOrdersPage />} />
            <Route path="/orders/created" element={<CreatedOrdersPage />} />
            <Route
              path="/orders/:id/history"
              element={
                <RequireRole roles={["ADMIN"]}>
                  <OrderHistoryPage />
                </RequireRole>
              }
            />
            <Route
              path="/orders/:id/history/:eventId"
              element={
                <RequireRole roles={["ADMIN"]}>
                  <OrderHistoryEventPage />
                </RequireRole>
              }
            />
            <Route
              path="/orders/:id/import"
              element={
                <RequireRole roles={["ADMIN", "STAFF"]}>
                  <ManifestImportPage />
                </RequireRole>
              }
            />
            <Route path="/orders/:id" element={<OrderDetailPage />} />
            <Route path="/orders/:id/invoice" element={<OrderDocPage doc="invoice" />} />
            <Route path="/orders/:id/quote" element={<OrderDocPage doc="quote" />} />
            <Route path="/orders/:id/submit" element={<SubmitOrderPage />} />

            <Route path="/orders/:id/scan_sessions" element={<OrderScanSessionsPage />} />

            {/* Invoices */}
            <Route path="/invoices" element={<InvoiceListPage />} />
            <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
            <Route path="/invoices/milestone/:id/pay" element={<MilestonePayPage />} />

            {/* Billing — Project invoices (OMS-6) */}
            <Route path="/billing/project-invoices" element={<ProjectInvoiceListPage />} />
            <Route path="/billing/project-invoices/:id" element={<ProjectInvoiceDetailPage />} />

            {/* Payments */}
            <Route path="/payment/receipts" element={<PaymentReceiptsPage />} />

            {/* Catalog */}
            <Route path="/catalog/items" element={<ProductsPage />} />
            <Route path="/catalog/attachments" element={<AttachmentsPage />} />
            <Route path="/catalog/premiums/door_styles" element={<DoorStylesPage />} />
            <Route path="/catalog/premiums/wood_species" element={<WoodSpeciesPage />} />
            <Route path="/catalog/premiums/finish_options" element={<FinishOptionsPage />} />
            <Route path="/catalog/premiums/finished_sides" element={<FinishedSidesPage />} />
            <Route path="/catalog/premiums/construction_methods" element={<ConstructionMethodsPage />} />
            <Route path="/catalog/public" element={<PublicCatalogPage />} />
            <Route path="/catalog/public/category/:id" element={<CategoryDetailPage />} />
            <Route path="/catalog/public/subcategory/:id" element={<SubcategoryDetailPage />} />
            <Route path="/catalog/2020/upload" element={<Upload2020Page />} />

            {/* Finance */}
            <Route path="/finance/reports/aging" element={<AgingReportPage />} />
            <Route path="/finance/reports/revenue" element={<RevenueReportPage />} />
            <Route path="/finance/reports/monthly-close" element={<MonthlyClosePage />} />
            <Route path="/finance/payments" element={<PaymentConfigPage />} />
            <Route path="/finance/discounts" element={<DiscountConfigPage />} />

            {/* Scanning */}
            <Route path="/scan" element={<ScanSessionsPage />} />
            <Route path="/scan/new" element={<NewScanPage />} />
            <Route path="/scan/session/:id" element={<ScanSessionDetailPage />} />
            <Route path="/scan/session/:id/scan" element={<ActiveScanPage />} />

            {/* Settings */}
            <Route path="/settings/profile" element={<ProfilePage />} />
            <Route path="/settings/address" element={<AddressesPage />} />
            <Route path="/settings/global" element={<GlobalSettingsPage />} />
            <Route path="/settings/users/all" element={<AllUsersPage />} />
            <Route path="/settings/users/staff" element={<StaffUsersPage />} />
            <Route path="/settings/users/requests" element={<AccessRequestsPage />} />
            <Route path="/settings/users/company/:id/invoices" element={<CompanyInvoicesPage />} />
            <Route path="/settings/users/company/:id" element={<CompanyDetailPage />} />
            <Route path="/settings/users/:id/invoices" element={<UserInvoicesPage />} />
            <Route path="/settings/users/:id" element={<UserDetailPage />} />

            {/* Projects (OMS v2) */}
            <Route path="/projects" element={<ProjectsListPage />} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/projects/:id/versions" element={<ProjectVersionsPage />} />
            <Route path="/projects/:id/versions/proposals/new" element={<ProjectVersionsPage />} />
            <Route path="/projects/:id/versions/proposals/:versionNumber" element={<ProjectVersionsPage />} />
            <Route path="/projects/:id/versions/change-orders/new" element={<ProjectVersionsPage />} />
            <Route path="/projects/:id/versions/change-orders/:changeOrderId" element={<ProjectVersionsPage />} />
            <Route path="/projects/:id/proposals" element={<ProjectVersionsPage />} />
            <Route path="/projects/:id/proposals/new" element={<ProjectVersionsPage />} />
            <Route path="/projects/:id/proposals/:versionNumber" element={<ProjectVersionsPage />} />
            <Route path="/projects/:id/change-orders" element={<ProjectVersionsPage />} />
            <Route path="/projects/:id/change-orders/:changeOrderId" element={<ProjectVersionsPage />} />

            {/* Proposal Templates (admin-only) */}
            <Route
              path="/settings/proposal-templates"
              element={
                <RequireRole roles={["ADMIN"]}>
                  <ProposalTemplateSettingsPage />
                </RequireRole>
              }
            />
            <Route
              path="/settings/proposal-templates/new"
              element={
                <RequireRole roles={["ADMIN"]}>
                  <ProposalTemplateEditPage />
                </RequireRole>
              }
            />
            <Route
              path="/settings/proposal-templates/:id"
              element={
                <RequireRole roles={["ADMIN"]}>
                  <ProposalTemplateEditPage />
                </RequireRole>
              }
            />
            {/* Back-compat: old singular path redirects to the list. */}
            <Route
              path="/settings/proposal-template"
              element={<Navigate to="/settings/proposal-templates" replace />}
            />

            {/* External Apps (admin-only) - API keys, webhooks, and OMS login */}
            <Route
              path="/settings/api-keys"
              element={
                <RequireRole roles={["ADMIN"]}>
                  <ApiKeysPage />
                </RequireRole>
              }
            />
            <Route
              path="/settings/api-keys/:id"
              element={
                <RequireRole roles={["ADMIN"]}>
                  <ClientDetailPage />
                </RequireRole>
              }
            />

            {/* Customers (OMS v2) */}
            <Route path="/crm/accounts" element={<CustomerAccountsPage />} />
            <Route path="/crm/accounts/new" element={<CustomerAccountCreatePage />} />
            <Route path="/crm/accounts/:id" element={<CustomerAccountDetailPage />} />
            <Route path="/crm/contacts" element={<ContactsPage />} />
            <Route path="/crm/contacts/new" element={<ContactCreatePage />} />
            <Route path="/crm/contacts/:id" element={<ContactDetailPage />} />
            <Route path="/crm/roles" element={<ContactRolesPage />} />
            <Route path="/settings/email/announcement" element={<AnnouncementPage />} />

            {/* Planner */}
            <Route path="/planner/orders/manage" element={<PlannerPage />} />

            {/* Issues (AI-reported) */}
            <Route path="/issues" element={<IssuesListPage />} />
            <Route path="/issues/:id" element={<IssueDetailPage />} />

            {/* Prompt admin (admin-only) */}
            <Route
              path="/settings/prompts"
              element={
                <RequireRole roles={["ADMIN"]}>
                  <PromptsListPage />
                </RequireRole>
              }
            />
            <Route
              path="/settings/prompts/:key"
              element={
                <RequireRole roles={["ADMIN"]}>
                  <PromptDetailPage />
                </RequireRole>
              }
            />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
